/// <amd-module name="Environments/ProviderCreateDetails/Kubernetes/Components/AddAzureKubernetesDetailsComponent" />
import * as React from "react";

import { StoreManager } from "Environments/Flux/StoreManager";
import * as Resources from "Environments/ProviderCreateDetails/Kubernetes/Resources";
import { Dropdown } from "VSSUI/Dropdown";
import { css } from "VSSUI/Util";

import { IAzureKubernetesProviderData, IKubernetesCluster } from "Environments/ProviderCreateDetails/Kubernetes/Types";
import { AzureSubscription } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { SubscriptionPicker, SubscriptionConsumer, ISubscriptionContextValue, Subscription } from "DistributedTask/Subscriptions/SubscriptionPicker";

import { ServiceEndpointsStore } from "Environments/ProviderCreateDetails/Kubernetes/Stores/ServiceEndpointsStore";
import { ServiceEndpointsActionsCreator } from "Environments/ProviderCreateDetails/Kubernetes/ActionsCreators/ServiceEndpointsActionsCreator";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";

import { KubernetesEvents } from "Environments/ProviderCreateDetails/Kubernetes/Utilities";

import { format } from "VSS/Core/Util/String";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { RadioButtonGroup, RadioButton, RadioButtonGroupDirection } from "VSSUI/RadioButton";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { publishTelemetry, FeatureNames } from "Environments/ProviderCreateDetails/Kubernetes/Telemetry";
import { Action } from "Environments/ProviderCreateDetails/Kubernetes/Utilities";
import { AccessTokenStore } from "Environments/ProviderCreateDetails/Kubernetes/Stores/AccessTokenStore";
import { IListBoxItem } from "VSSUI/ListBox";
import { ListSelection } from "VSSUI/List";

interface IAddAzureKubernetesDetailsState {
    clusters: IKubernetesCluster[] | undefined;
    fetchingMoreClusters: boolean;
    namespaces: string[] | undefined;
    fetchingMoreNamespaces: boolean;
    createNewNamespace: boolean;
    newNamespace: string;
    existingNamespace: string;
    newProviderData: IAzureKubernetesProviderData;
}

interface IAddAzureKubernetesDetailsProps {
    instanceId: string;
    redirectUrl: string | undefined;
    aadTenantId: string | undefined;
    providerData: IAzureKubernetesProviderData;
    disableAll: boolean;
    isEditMode?: boolean;
    onProviderDataChange: (providerData: IAzureKubernetesProviderData, isValid: boolean) => void;
    isNewSubscriptionCreated: (isSubscriptionCreated: boolean) => void;
}


const getLoadingComponent = (label?: string) => (
    <Spinner
        label={label}
        size={SpinnerSize.medium}
        className="default-margin-top"
    />
);

export class AddAzureKubernetesDetailsComponent extends VssComponent<IAddAzureKubernetesDetailsProps, IAddAzureKubernetesDetailsState> {

    constructor(props: IAddAzureKubernetesDetailsProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            clusters: undefined,
            fetchingMoreClusters: false,
            namespaces: undefined,
            fetchingMoreNamespaces: false,
            newProviderData: props.providerData,
            createNewNamespace: false,
            newNamespace: "",
            existingNamespace: ""
        };

        this._serviceEndpointsStore = StoreManager.GetStore<ServiceEndpointsStore>(ServiceEndpointsStore, this.props.instanceId);
        this._serviceEndpointsActionCreator = ActionCreatorManager.GetActionCreator<ServiceEndpointsActionsCreator>(ServiceEndpointsActionsCreator, this.props.instanceId);
        this._serviceEndpointsStore.addListener(KubernetesEvents.CLUSTERS_FETCHED_EVENT, this._onClustersFetched);
        this._serviceEndpointsStore.addListener(KubernetesEvents.NAMESPACES_FETCHED_EVENT, this._onNamespacesFetched);
    }

    public render(): JSX.Element {
        return (
            <div className="flex-column">
                {
                    this.props.isEditMode ? (
                        <FormItem
                            className={"default-margin-top"}
                            label={Resources.SubscriptionLabel}>
                            <TextField
                                value={format("{0} ({1})", this.state.newProviderData.subscription!.displayName, this.state.newProviderData.subscription!.subscriptionId)}
                                disabled={true}
                            />
                        </FormItem>
                    )
                        : (<SubscriptionConsumer>
                            {
                                (subscriptionContext: ISubscriptionContextValue) => {
                                    const isLoadComplete = subscriptionContext.state && subscriptionContext.state.loadedAllSubscriptions;
                                    const subscriptions: Subscription[] | undefined = isLoadComplete ? subscriptionContext.state.subscriptions : undefined;
                                    if (!this.state.newProviderData.subscription && subscriptions && subscriptions[0]) {
                                        setTimeout(() => {
                                            this._onSubscriptionChanged(subscriptions[0]);
                                        })
                                    }
                                    this.props.isNewSubscriptionCreated(isLoadComplete && !(subscriptions && subscriptions.length));

                                    return (
                                        <SubscriptionPicker
                                            disabled={this.props.disableAll}
                                            useDropdown={true}
                                            dropdownCalloutClassName={"k8s-aks-sub-callout"}
                                            label={Resources.SubscriptionLabel}
                                            showCreateSubscriptionButton={isLoadComplete && !(subscriptions && subscriptions.length)}
                                            subscriptionContext={subscriptionContext}
                                            onSubscriptionChoiceChanged={this._onSubscriptionChanged}
                                            selectedSubscriptionId={this._getSubscriptionSelectedKey()}
                                            createSubscriptionReplyTo={this.props.redirectUrl}
                                            className="environment-ComboBox default-margin-top"
                                        />);
                                }}
                        </SubscriptionConsumer>)
                }
                {
                    this.state.newProviderData.subscription
                    && this._getClusterPicker()
                }

                {
                    this.state.newProviderData.cluster
                    && this._getNamespacePicker()
                }
            </div>
        );
    }

    public componentDidUpdate(prevProps: IAddAzureKubernetesDetailsProps, prevState: IAddAzureKubernetesDetailsState): void {
        if (this.state.newProviderData.kubernetesNamespace !== prevState.newProviderData.kubernetesNamespace) {
            this.props.onProviderDataChange(this.state.newProviderData, this._isValid(this.state.newProviderData));
        }
    }

    public componentWillUnmount(): void {
        this._serviceEndpointsStore.removeListener(KubernetesEvents.CLUSTERS_FETCHED_EVENT, this._onClustersFetched);
        this._serviceEndpointsStore.removeListener(KubernetesEvents.NAMESPACES_FETCHED_EVENT, this._onNamespacesFetched);
        super.componentWillUnmount();
    }

    private _getClusterPicker(): React.ReactNode {
        if (this.props.isEditMode || this.props.disableAll) {
            if (this.state.newProviderData.cluster) {
                const cluster = this.state.newProviderData.cluster;
                return (<FormItem
                    className={"default-margin-top"}
                    label={Resources.Cluster}>
                    <TextField
                        value={cluster.resourceGroup ? format("{0} ({1})", cluster.name, cluster.resourceGroup) : cluster.name}
                        disabled={true}
                    />
                </FormItem>);

            }
            else {
                return;
            }
        }

        return (this.state.clusters ? (
            this.state.clusters.length ? (
                <>
                    <FormItem
                        label={Resources.Cluster}
                        className={"default-margin-top k8s-aks-dropdown-form-item flex-grow"}
                    >
                        <Dropdown
                            items={this._getClusterOptions()}
                            onSelect={this._onClusterChanged}
                            selection={this._clusterSelection}
                            className="k8s-aks-dropdown"
                            calloutContentClassName={"k8s-aks-dropdown-callout"}
                        />
                    </FormItem>
                    {this.state.fetchingMoreClusters && getLoadingComponent(Resources.LoadingMore)}
                </>)
                : <span className="default-margin-top">{Resources.NoClustersMessage}</span> // When we have create cluster experience, have a create cluster button here, or directly show an experience for creating a cluster
        )
            : getLoadingComponent(Resources.LoadingClusters))
    }

    private _getNamespacePicker(): React.ReactNode {
        if (this.props.isEditMode || this.props.disableAll) {
            if (this.state.newProviderData.kubernetesNamespace) {
                return (<FormItem
                    className={"default-margin-top"}
                    label={Resources.Namespace}>
                    <TextField
                        value={this.state.newProviderData.kubernetesNamespace}
                        disabled={true}
                    />
                </FormItem>);
            }
            else {
                return;
            }
        }

        const getNamespaceDropdown = (showIndependently: boolean) => (
            <>
                <FormItem
                    label={showIndependently ? Resources.Namespace : ""}
                    className={css("k8s-aks-dropdown-form-item", "flex-grow", showIndependently ? "default-margin-top" : "ns-picker-with-radio")}
                >
                    <Dropdown
                        ariaLabel={Resources.Namespace}
                        items={this._getNamespaceOptions()}
                        onSelect={this._onNamespaceOptionChanged}
                        selection={this._namespaceSelection}
                        className={"k8s-aks-dropdown"}
                        calloutContentClassName={"k8s-aks-dropdown-callout"}
                    />
                </FormItem>
                {this.state.fetchingMoreNamespaces && getLoadingComponent(Resources.LoadingMore)}
            </>);

        if (!this.state.namespaces) {
            return getLoadingComponent(Resources.LoadingNamespaces);
        }

        const errorMessageForNewNamespace = this._getNewNamespaceErrorMessage(this.state.newProviderData.kubernetesNamespace);

        const getNamespaceTextField = (showIndependently: boolean) => (
            <FormItem
                className={showIndependently ? "default-margin-top" : "ns-picker-with-radio"}
                label={showIndependently ? Resources.CreateNamespace : ""}
                error={!!errorMessageForNewNamespace}
                message={errorMessageForNewNamespace}>
                <TextField
                    value={this.state.newProviderData.kubernetesNamespace}
                    disabled={this.props.disableAll}
                    onChange={this._onNamespaceTextChanged}
                    ariaLabel={Resources.NewNamespaceName}
                />
            </FormItem>);

        if (!this.state.namespaces.length) {
            return getNamespaceTextField(true);
        }

        return (this.state.namespaces
            ?
            (<div className="default-margin-top">
                <RadioButtonGroup
                    selectedButtonId={this.state.createNewNamespace ? newNamespaceKey : existingNamespaceKey}
                    onSelect={this._onNamespaceOriginChanged}
                    text={Resources.Namespace}
                    direction={RadioButtonGroupDirection.Horizontal}
                    className={"ns-picker-radio-group"}
                >
                    <RadioButton id={newNamespaceKey} key={newNamespaceKey} text={Resources.New} disabled={this.props.disableAll} />
                    <RadioButton id={existingNamespaceKey} key={existingNamespaceKey} text={Resources.Existing} className={"existing-ns-radio"} disabled={this.props.disableAll} />
                </RadioButtonGroup>
                {
                    this.state.createNewNamespace ? getNamespaceTextField(false) : getNamespaceDropdown(false)
                }
            </div>)
            : getLoadingComponent(Resources.LoadingNamespaces));
    }

    private _getSubscriptionSelectedKey(): string | undefined {
        return (
            this.state.newProviderData.subscription && this.state.newProviderData.subscription.subscriptionId
        )
    }

    private _getClusterOptions(): IListBoxItem[] {
        let options: IListBoxItem[] = [];

        if (this.state.clusters) {
            this.state.clusters.forEach(cluster => {
                options.push({
                    id: cluster.id,
                    text: cluster.resourceGroup ? format("{0} ({1})", cluster.name, cluster.resourceGroup) : cluster.name
                })
            });
        }

        return options;
    }

    private _getNamespaceOptions(): IListBoxItem[] {
        let options: IListBoxItem[] = [];

        if (this.state.namespaces) {
            this.state.namespaces.forEach(namespace => {
                options.push({
                    id: namespace,
                    text: namespace
                })
            })
        }

        return options;
    }

    private _onClustersFetched = (): void => {
        const clusters: IKubernetesCluster[] = this._serviceEndpointsStore.getState().clusters;
        const state: IAddAzureKubernetesDetailsState = this.state;
        state.clusters = clusters;
        // Need to remove afer 1-2 sprint
        publishTelemetry(this.context.pageContext, FeatureNames.K8sSubExistingCluster, { "IsExistingCluster": clusters && clusters.length > 0 ? "True" : "False" });
        publishTelemetry(this.context.pageContext, FeatureNames.EnvironmentCreationWorkFlow, { "Action": Action.AddAKSCluster, "IsExistingCluster": clusters && clusters.length > 0 ? "True" : "False" });
        const prevFetchState = state.fetchingMoreClusters;
        const callbackParams = this._serviceEndpointsStore.getState().clustersCallbackParameters;
        state.fetchingMoreClusters = !!callbackParams && !!callbackParams.skipToken;

        if (!state.newProviderData.cluster) {
            this._clusterSelection.select(0);
            state.newProviderData.cluster = clusters[0];
            this._setSelectedCluster(state);
        }
        else {
            this.setState(state);
        }
    }

    private _onNamespacesFetched = (): void => {
        const namespaces: string[] = this._serviceEndpointsStore.getState().namespaces || [];
        const callbackParams = this._serviceEndpointsStore.getState().namespacesCallbackParameters;
        const fetchingMoreNamespaces = !!callbackParams && !!callbackParams.skipToken;
        let updatedData = Object.assign({}, this.state.newProviderData);
        // Need to remove afer 1-2 sprint
        publishTelemetry(this.context.pageContext, FeatureNames.K8sSubExistingNamespace, { "IsExistingNamesapce": namespaces && namespaces.length > 0 ? "True" : "False" });
        publishTelemetry(this.context.pageContext, FeatureNames.EnvironmentCreationWorkFlow, {"Action": Action.AddAKSNamespace, "IsExistingNamesapce": namespaces && namespaces.length > 0 ? "True" : "False" });
        const createNewNamespace = !namespaces.length;
        updatedData.createNamespace = createNewNamespace
        if (!createNewNamespace && !updatedData.kubernetesNamespace) {
            this._namespaceSelection.select(0);
            updatedData.kubernetesNamespace = namespaces[0];
        }

        this.setState({ namespaces: namespaces, newProviderData: updatedData, createNewNamespace: createNewNamespace, existingNamespace: namespaces[0], fetchingMoreNamespaces: fetchingMoreNamespaces });
    }

    private _onSubscriptionChanged = (subscription: Subscription | undefined): void => {
        const azureSubscription = subscription
            ? {
                displayName: subscription.displayName,
                subscriptionId: subscription.id,
                subscriptionTenantId: subscription.subscriptionAccount.subscriptionTenantId,
            } as AzureSubscription
            : undefined;

        let updatedData = Object.assign({}, this.state.newProviderData);

        if (azureSubscription) {
            updatedData.createNewEndpoint = true;
            updatedData.endpointId = undefined; // important
            updatedData.subscription = azureSubscription;
        }

        // Important: Reset currently selected cluster and namespace
        updatedData.cluster = undefined;
        updatedData.kubernetesNamespace = undefined;

        publishTelemetry(this.context.pageContext, FeatureNames.K8sSubscriptionChanged);

        this.setState(
            (state, props) => ({ clusters: undefined, namespaces: undefined, newProviderData: updatedData }),
            () => {
                setTimeout(() =>
                    this._serviceEndpointsActionCreator.getAzureKubernetesClusters(
                        this.context.pageContext,
                        azureSubscription,
                        this.props.aadTenantId,
                        this._getCachedAccessTokens())
                    , 0);
            }
        )
    }

    private _onClusterChanged = (event: any, selectedOption: IListBoxItem): void => {
        const cluster: IKubernetesCluster = this.state.clusters!.find((cluster) => { return cluster.id == selectedOption.id })!;

        // Important: Reset currently selected namespace
        const state: IAddAzureKubernetesDetailsState = this.state;
        state.newProviderData.kubernetesNamespace = undefined;
        state.newNamespace = "";
        state.newProviderData.cluster = cluster;

        publishTelemetry(this.context.pageContext, FeatureNames.K8sSubClusterFilled);
        this._setSelectedCluster(state);
    }

    private _setSelectedCluster(updatedState: IAddAzureKubernetesDetailsState): void {
        const subscription: AzureSubscription | undefined = this.state.newProviderData.subscription;

        const cluster: IKubernetesCluster | undefined = updatedState.newProviderData.cluster;
        updatedState.namespaces = undefined;

        if (cluster) {
            updatedState.newProviderData.clusterName = cluster.name;
        }

        this.setState(
            (state, props) => (updatedState),
            () => {
                setTimeout(() => {
                    if (cluster) {
                        this._serviceEndpointsActionCreator.getAzureKubernetesNamespaces(
                            this.context.pageContext,
                            subscription,
                            this.props.aadTenantId,
                            this._getCachedAccessTokens(),
                            cluster.id,
                            cluster.fqdn)
                    }
                }, 0);
            }
        );
    }

    private _onNamespaceOptionChanged = (event: any, selectedOption: IListBoxItem): void => {
        let updatedData = Object.assign({}, this.state.newProviderData);
        const namespace = selectedOption.id as string;
        updatedData.kubernetesNamespace = namespace
        publishTelemetry(this.context.pageContext, FeatureNames.K8sSubNamespaceFilled);

        this.setState({ newProviderData: updatedData, existingNamespace: namespace });
    }

    private _onNamespaceTextChanged = (event: any, namespace: string): void => {
        let updatedData = Object.assign({}, this.state.newProviderData);
        updatedData.kubernetesNamespace = namespace;
        this.setState({ newProviderData: updatedData, newNamespace: namespace });
    }

    private _onNamespaceOriginChanged = (id: string): void => {
        let updatedData = Object.assign({}, this.state.newProviderData);
        let createNewNamespace = false;
        if (id === newNamespaceKey) {
            createNewNamespace = true;
            updatedData.kubernetesNamespace = this.state.newNamespace;
        }
        else {
            updatedData.kubernetesNamespace = this.state.existingNamespace;
        }

        updatedData.createNamespace = createNewNamespace;
        this.setState({ createNewNamespace: createNewNamespace, newProviderData: updatedData });
    }

    private _isValid(data: IAzureKubernetesProviderData): boolean {
        return !!(data.subscription && data.cluster && data.kubernetesNamespace) && !(this.state.createNewNamespace && this._getNewNamespaceErrorMessage(data.kubernetesNamespace));
    }

    private _getNewNamespaceErrorMessage(namespace: string | undefined): string {
        return (
            namespace
            && !(/^[a-z0-9]$|^([a-z0-9][a-z0-9-]*[a-z0-9])$/).test(namespace))
            ? Resources.InvalidNamespace
            : (this.state.namespaces && this.state.namespaces.some(n => n === namespace))
                ? Resources.NamespaceExists
                : "";
    }

    private _getCachedAccessTokens(): { [key: string]: string } {
        return StoreManager.GetStore<AccessTokenStore>(AccessTokenStore, this.props.instanceId).getState();
    }

    private _serviceEndpointsStore: ServiceEndpointsStore;
    private _serviceEndpointsActionCreator: ServiceEndpointsActionsCreator;
    private _clusterSelection = new ListSelection({ selectOnFocus: false, multiSelect: false });
    private _namespaceSelection = new ListSelection({ selectOnFocus: false, multiSelect: false });
}

const newNamespaceKey = "new-namespace";
const existingNamespaceKey = "existing-namespace";